import { useMutation } from "@apollo/client/react";
import { useRef, useState } from "react";
import Link from "next/link";
import { Formik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { FunnelButton } from "../../FunnelButton";
import { FunnelBackButton } from "../../FunnelBackButton";
import { FormFunnelDisclaimer } from "./FormFunnelDisclaimer";
import { FormFieldError } from "@portal/app/_components/ui-elements/Form";
import { logger } from "@portal/app/_common/lib/logger";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useFormFunnelTracking } from "../../hooks/useFormFunnelTracking";
import { SubmitFormFunnelLeadDocument } from "../../hooks/generated";
import { ConfirmStepProps, isCompleteCustomerData } from "./types";
import { buildCustomerData } from "./buildCustomerData";
import { trackHostedLeadConversion } from "./trackHostedLeadConversion";
import { toRelativePath } from "../../utils/urlUtils";
import { useHostedStepAction } from "./useHostedStepAction";
import { useFormFunnelHeadingLevel } from "../../FormFunnelHeadingContext";

const ELIGIBILITY_FAILURE_MESSAGES: Record<string, string> = {
  ZIP_CODE_RESTRICTED: "We couldn't match you with this program based on your location.",
  CONFLICT_EXISTING_ENROLLMENT: "You already have an active application for this program.",
  STATE_RESTRICTED: "Unfortunately, this program is not available in your state.",
  EDUCATION_LEVEL_NOT_ELIGIBLE: "Your education level does not meet this program's requirements.",
  AGE_RESTRICTED: "You must be at least 18 years old to apply for this program.",
  MISSING_REQUIRED_FIELDS:
    "Some required information is missing. Please go back and complete all fields.",
  HIGH_SCHOOL_GRAD_YEAR_RESTRICTED:
    "Your high school graduation year does not meet this program's requirements.",
  PROGRAM_SUPPRESSED: "This school isn't taking new inquiries right now.",
};

const DEFAULT_CTA_TEXT = "Get Info";
const PROGRAM_UNAVAILABLE_MESSAGE = "This program is unavailable. Please choose another program.";

/**
 * Best-effort delay to let fire-and-forget tracking beacons (Mixpanel, Everflow,
 * Facebook) flush before we navigate away for a post-lead redirect. 100ms is
 * sufficient on warm connections; cold starts / slow networks may still drop
 * beacons. If we ever need reliable flushing, switch trackers to
 * `navigator.sendBeacon` or expose a flush promise.
 *
 * Exported so the DOM tests can wait slightly longer than this delay without
 * hard-coding a duplicate value.
 */
export const TRACKING_FLUSH_DELAY_MS = 100;

export const ConfirmStep = ({
  program,
  user,
  customerDataRef,
  funnelId,
  formFunnelSessionId,
  answers,
  searchUrl = "/explore",
  onComplete,
  onBack,
  isPreview = false,
  ctaButtonText,
  postLeadRedirectMode,
  prePingResult,
  selectedResultRank,
  onSubmittingChange,
  onValidatedSubmit,
  programSummary,
}: ConfirmStepProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const { getTrackingIds, trackFormSubmit, trackEvent } = useFormFunnelTracking();
  const [submitFormFunnelLead] = useMutation(SubmitFormFunnelLeadDocument);
  const [eligibilityWarning, setEligibilityWarning] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isProgramUnavailable, setIsProgramUnavailable] = useState(false);
  const action = useHostedStepAction(program.id);
  const trackedEnrollmentIds = useRef(new Set<string>());

  return (
    <Formik
      initialValues={{ disclaimer: false }}
      validationSchema={Yup.object({
        disclaimer: Yup.boolean().oneOf([true], "You must accept the terms").required(),
      })}
      onSubmit={async (_values, { setSubmitting }) => {
        const generation = action.begin();
        if (generation === undefined) return;
        setSubmitError(null);
        onSubmittingChange?.(true);
        try {
          // In preview mode, skip the actual lead submission
          if (isPreview) {
            // Simulate a short delay for UX consistency
            await new Promise((resolve) => setTimeout(resolve, 500));
            if (onComplete) onComplete(undefined, customerDataRef.current.nameFirst);
            return;
          }

          // Get all tracking IDs from the tracking provider
          const trackingIds = await getTrackingIds();

          // Build the compliance tracking input
          const complianceTracking = {
            jornayaLeadId: trackingIds.jornayaLeadId || "",
            trustedFormCertId: trackingIds.trustedFormCertId,
            // Forwarded because in-app browsers drop the SDK's ef_* cookies from
            // requests mid-funnel; the server reads its own cookie first, then this.
            everflowTransactionId: trackingIds.everflowTransactionId,
            fbPixelBrowserId: trackingIds.facebookBrowserId,
            fbPixelClickId: trackingIds.facebookClickId,
            marketplaceEventId: trackingIds.marketplaceEventId,
            gaClientId: trackingIds.gaClientId,
            googleClickId: trackingIds.googleClickId,
            googleWbraid: trackingIds.googleWbraid,
            googleGbraid: trackingIds.googleGbraid,
            googleAdsCapturedAt: trackingIds.googleAdsCapturedAt,
            everflowOfferId: trackingIds.everflowOfferId,
            everflowAffiliateId: trackingIds.everflowAffiliateId,
            everflowSub1: trackingIds.everflowSub1,
            everflowSub2: trackingIds.everflowSub2,
            everflowSub3: trackingIds.everflowSub3,
            everflowSub4: trackingIds.everflowSub4,
            everflowSub5: trackingIds.everflowSub5,
            everflowSub6: trackingIds.everflowSub6,
            everflowSub7: trackingIds.everflowSub7,
            everflowSub8: trackingIds.everflowSub8,
          };

          // Build customer data from collected steps
          const builderData = customerDataRef.current;
          if (!isCompleteCustomerData(builderData)) {
            logger.error(
              {
                hasEducationLevel: !!builderData.educationLevel,
                hasDob: !!builderData.dob,
              },
              "ConfirmStep: incomplete customer data at submission"
            );
            setSubmitError(
              "Some required information is missing. Please go back and complete all fields."
            );
            return;
          }
          const customerData = buildCustomerData(builderData);

          // Submit using the new form funnel lead mutation
          const { data } = await submitFormFunnelLead({
            variables: {
              funnelId,
              programId: program.id,
              answers: answers,
              formFunnelSessionId,
              customerData,
              complianceTracking,
              disclaimer: program.customDisclaimer || "Terms accepted",
              ...(prePingResult != null ? { prePingResult } : {}),
              ...(selectedResultRank ? { selectedResultRank } : {}),
            },
          });

          const result = data?.submitFormFunnelLead;
          if (result?.scenario === "PROGRAM_UNAVAILABLE") {
            setIsProgramUnavailable(true);
            return;
          }

          // Check for eligibility failure
          const failure = result?.eligibilityFailure;
          if (failure) {
            const message =
              ELIGIBILITY_FAILURE_MESSAGES[failure] ??
              "We encountered an issue processing your application. Please contact support.";
            setEligibilityWarning(message);
            return;
          }

          let hostedLeadTracking: Promise<void> | undefined;
          trackFormSubmit();
          if (result?.success && result.enrollmentItemId) {
            if (
              action.isCurrent(generation) &&
              (result.scenario === "HOSTED" ||
                (result.scenario === "CLICKOUT" && result.redirectUrl)) &&
              !trackedEnrollmentIds.current.has(result.enrollmentItemId)
            ) {
              trackedEnrollmentIds.current.add(result.enrollmentItemId);
              onValidatedSubmit?.(result.scenario);
            }
            if (result.scenario === "HOSTED") {
              hostedLeadTracking = trackHostedLeadConversion({
                enrollmentItemId: result.enrollmentItemId,
                funnelId,
                programId: program.id,
                providerId: program.provider.id,
              });
            }

            trackEvent(
              "Form Funnel - Enrollment Linked",
              {
                enrollmentItemId: result.enrollmentItemId,
                formFunnelId: funnelId,
                formFunnelSessionId,
                programId: program.id,
                providerId: program.provider.id,
                scenario: result.scenario,
              },
              { transport: "sendBeacon", send_immediately: true }
            );
          }

          trackEvent("Form Funnel - Hosted Step Completed", { stepType: "confirm" });

          const postLeadRedirectUrl = result?.redirectUrl;
          if (postLeadRedirectUrl) {
            if (postLeadRedirectMode === "showSuccessThenRedirect") {
              if (onComplete) onComplete(postLeadRedirectUrl, customerDataRef.current.nameFirst);
              return;
            }
            // Wait for GTM (bounded by its application timeout) and allow the
            // other tracking beacons their existing flush delay before leaving.
            await Promise.all([
              hostedLeadTracking,
              new Promise((resolve) => setTimeout(resolve, TRACKING_FLUSH_DELAY_MS)),
            ]);
            window.location.href = getUrls().LeadRedirect(postLeadRedirectUrl);
            return;
          }

          if (onComplete) onComplete(undefined, customerDataRef.current.nameFirst);
        } catch (e) {
          logger.error(e);
          setSubmitError("Something went wrong submitting your application. Please try again.");
        } finally {
          action.finish(generation);
          setSubmitting(false);
          onSubmittingChange?.(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, isValid }) => (
        <form
          onSubmit={handleSubmit}
          data-tf-element-role="offer"
          data-testid="hosted-confirm-step"
        >
          {/* Locked during submission: backing up mid-flight would allow a
              second submission while the first mutation is still running. */}
          {onBack && (
            <FunnelBackButton
              appearance="text"
              onClick={onBack}
              disabled={isSubmitting}
              sx={{ mb: 1.5 }}
            >
              Back
            </FunnelBackButton>
          )}

          {programSummary}

          <Typography
            component={headingLevel}
            sx={{
              color: "text.primary",
              fontSize: "1.5rem",
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
              mb: 2,
            }}
          >
            Final step
          </Typography>

          {eligibilityWarning && (
            <Alert severity="warning" sx={{ mb: 3 }} data-testid="eligibility-warning">
              {eligibilityWarning}
            </Alert>
          )}

          {isProgramUnavailable && (
            <Alert severity="warning" sx={{ mb: 3 }} data-testid="program-unavailable-warning">
              {PROGRAM_UNAVAILABLE_MESSAGE}
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }} data-testid="submit-error">
              {submitError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <FormFunnelDisclaimer
              disclaimer={program.customDisclaimer}
              advertiserName={program.provider.advertiserName}
              isTrustedFormEnabled={program.provider.isTrustedFormEnabled}
              contact={{
                // Prefer the locally collected phone (PhoneStep writes it to the
                // ref immediately on verification) and fall back to user.phone
                // from the GraphQL viewer — mirrors the other three fields and
                // avoids a race where a stale viewer leaves {{phone_number}}
                // unresolved on Confirm.
                phone: customerDataRef.current.phone || user?.phone || null,
                firstName: customerDataRef.current.nameFirst || null,
                lastName: customerDataRef.current.nameLast || null,
                email: customerDataRef.current.email || null,
              }}
              schoolName={program.provider.shortName}
              answers={answers}
            />
            <FormFieldError name="disclaimer" />
          </Box>

          {isProgramUnavailable ? (
            <Link href={toRelativePath(searchUrl)} passHref legacyBehavior>
              <FunnelButton
                component="a"
                onClick={() =>
                  trackEvent("Form Funnel - Unavailable - Search Clicked", {
                    destinationUrl: searchUrl,
                    sessionId: formFunnelSessionId,
                  })
                }
                data-testid="view-all-programs"
                sx={{ maxWidth: "none", minHeight: 56, fontSize: "1.1rem" }}
              >
                View All Programs
              </FunnelButton>
            </Link>
          ) : (
            <FunnelButton
              type="submit"
              disabled={!isValid || isSubmitting}
              data-tf-element-role="submit"
              data-testid="hosted-confirm-submit"
              sx={{ maxWidth: "none", minHeight: 56, fontSize: "1.1rem" }}
            >
              {ctaButtonText || DEFAULT_CTA_TEXT}
            </FunnelButton>
          )}

          {/* Hidden fields for TrustedForm/Jornaya if needed by scripts scanning DOM */}
          <input type="hidden" id="leadid_token" name="universal_leadid" value="" />
        </form>
      )}
    </Formik>
  );
};
